import React from 'react';
import { User, Star, TrendingUp } from 'lucide-react';
import { Database } from '../lib/supabase';
import { useTheme } from '../hooks/useTheme';

type Child = Database['public']['Tables']['children']['Row'];

interface ChildCardProps {
  child: Child;
  onClick: () => void;
  isSelected?: boolean;
}

export function ChildCard({ child, onClick, isSelected = false }: ChildCardProps) {
  const { isDarkMode } = useTheme();
  const level = Math.floor(child.total_points / 100) + 1;
  const progressToNextLevel = (child.total_points % 100);

  return (
    <div
      onClick={onClick}
      className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:scale-105 ${
        isSelected
          ? 'border-blue-500 shadow-lg'
          : isDarkMode
          ? 'border-gray-600 bg-gray-800 hover:border-blue-400 hover:shadow-md'
          : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
      } ${isSelected && isDarkMode ? 'bg-blue-900/20' : isSelected ? 'bg-blue-50' : ''}`}
    >
      <div className="flex items-center space-x-4">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl"
          style={{ backgroundColor: child.avatar_color }}
        >
          {child.name.charAt(0).toUpperCase()}
        </div>
        
        <div className="flex-1">
          <h3 className={`text-lg font-semibold ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {child.name}
          </h3>
          <p className={`text-sm ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Age {child.age}
          </p>
          
          <div className="mt-2 space-y-2">
            <div className="flex items-center space-x-2">
              <Star className="w-4 h-4 text-yellow-500" />
              <span className={`text-sm ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {child.total_points} points
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-4 h-4 text-green-500" />
              <span className={`text-sm ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Level {level}
              </span>
            </div>
          </div>
          
          {/* Progress to next level */}
          <div className="mt-3">
            <div className={`flex justify-between text-xs mb-1 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              <span>Progress to Level {level + 1}</span>
              <span>{progressToNextLevel}/100</span>
            </div>
            <div className={`w-full rounded-full h-2 ${
              isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
            }`}>
              <div
                className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressToNextLevel}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}